import type { Voice } from "@/types/vapi-voices";

export interface VoiceOption {
  voice_id: string;
  name: string;
  previewUrl?: string | null;
  is_custom?: boolean;
  description?: string | null;
  accent?: string | null;
  gender?: string | null;
  provider?: string | null;
}

const VOICE_PREVIEW_AVAILABLE = "Voice preview available";
const NO_VOICE_PREVIEW = "No voice preview available";
const CUSTOM_VOICE_DESCRIPTION = "Custom voice";

/** Slug → compact badge code (API may send either). */
const PROVIDER_CODES: Record<string, string> = {
  cartesia: "CTS",
  "11labs": "11L",
  openai: "OAI",
  hume: "HUME",
  azure: "AZ",
  deepgram: "DG",
  lmnt: "LMNT",
  neuphonic: "NEU",
  playht: "PHT",
  "rime-ai": "RIME",
  "smallest-ai": "SAI",
  tavus: "TAV",
  sesame: "SES",
  inworld: "INW",
  minimax: "MMX",
  xai: "XAI",
  vapi: "VAPI",
  "custom-voice": "CV",
};

const PROVIDER_NAMES: Record<string, string> = {
  cartesia: "Cartesia",
  cts: "Cartesia",
  "11labs": "ElevenLabs",
  "11l": "ElevenLabs",
  openai: "OpenAI",
  oai: "OpenAI",
  hume: "Hume",
  azure: "Azure",
  az: "Azure",
  deepgram: "Deepgram",
  dg: "Deepgram",
  lmnt: "LMNT",
  neuphonic: "Neuphonic",
  neu: "Neuphonic",
  playht: "PlayHT",
  pht: "PlayHT",
  "rime-ai": "Rime AI",
  rime: "Rime AI",
  "smallest-ai": "Smallest AI",
  sai: "Smallest AI",
  tavus: "Tavus",
  tav: "Tavus",
  sesame: "Sesame",
  ses: "Sesame",
  inworld: "Inworld",
  inw: "Inworld",
  minimax: "MiniMax",
  mmx: "MiniMax",
  xai: "xAI",
  vapi: "Vapi",
  "custom-voice": "Custom voice",
  cv: "Custom voice",
};

const KNOWN_PROVIDER_CODES = new Set(
  Object.values(PROVIDER_CODES).map((code) => code.toUpperCase())
);

/** Normalize a raw API provider string to a badge code (`XAI`, `11L`, …). */
export function normalizeProviderCode(provider?: string | null): string {
  if (!provider?.trim()) return "";
  const raw = provider.trim();
  const upper = raw.toUpperCase();
  if (KNOWN_PROVIDER_CODES.has(upper)) return upper;
  const key = raw.toLowerCase();
  return PROVIDER_CODES[key] ?? upper;
}

/** Filter key for a voice — custom clones bucket under `CV`. */
export function getProviderFilterKey(voice: VoiceOption): string {
  if (voice.is_custom) return "CV";
  return normalizeProviderCode(voice.provider);
}

/** Badge / filter label for a voice (`Custom`, `11L`, `XAI`, …). */
export function getProviderBadgeLabel(voice: VoiceOption): string {
  if (voice.is_custom) return "Custom";
  return getProviderFilterKey(voice);
}

/** Human-readable provider name from a slug or code. */
export function getProviderName(provider?: string | null): string {
  if (!provider?.trim()) return "";
  const key = provider.trim().toLowerCase();
  return PROVIDER_NAMES[key] ?? provider.trim();
}

/** Distinct provider filter keys present in the list. */
export function listProviderFilters(voices: VoiceOption[]): string[] {
  const seen = new Set<string>();
  const codes: string[] = [];
  for (const voice of voices) {
    const code = getProviderFilterKey(voice);
    if (!code || seen.has(code)) continue;
    seen.add(code);
    codes.push(code);
  }
  return codes;
}

export function matchesProvider(
  voice: VoiceOption,
  providerFilter: string | null
): boolean {
  if (!providerFilter) return true;
  return getProviderFilterKey(voice) === providerFilter;
}

/** Distinct accents present in the list (case-insensitive). */
export function listAccentFilters(voices: VoiceOption[]): string[] {
  const seen = new Set<string>();
  const accents: string[] = [];
  for (const voice of voices) {
    const accent = voice.accent?.trim();
    if (!accent) continue;
    const key = accent.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    accents.push(accent);
  }
  return accents.sort((a, b) => a.localeCompare(b));
}

export function matchesAccent(
  voice: VoiceOption,
  accentFilter: string | null
): boolean {
  if (!accentFilter) return true;
  return (voice.accent?.trim().toLowerCase() ?? "") === accentFilter.toLowerCase();
}

export function getVoiceGender(voice: VoiceOption): string {
  return voice.gender?.trim() ?? "";
}

/** Secondary line under the voice name in the list. */
export function getVoiceSubtitle(voice: VoiceOption): string {
  const description = voice.description?.trim();
  if (description) return description;
  if (voice.previewUrl) return VOICE_PREVIEW_AVAILABLE;
  if (voice.is_custom) return CUSTOM_VOICE_DESCRIPTION;
  return NO_VOICE_PREVIEW;
}

/** Search blob used by the combobox filter. */
export function buildVoiceSearchText(voice: VoiceOption): string {
  return [
    voice.name,
    voice.provider,
    getProviderFilterKey(voice),
    getProviderName(voice.provider),
    voice.accent,
    voice.gender,
    voice.description,
    voice.is_custom ? "custom" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export function toVoiceOption(voice: Voice): VoiceOption {
  return {
    voice_id: voice.voice_id,
    name: voice.name,
    previewUrl: voice.previewUrl ?? null,
    is_custom: Boolean(voice.is_custom),
    description: voice.description ?? null,
    accent: voice.accent ?? null,
    gender: voice.gender ?? null,
    provider: voice.provider ?? null,
  };
}

/** Resolve slider increment from params_schema — prefer provider `step`. */
export function getParamStep(
  schema: { type?: string; step?: number | string; min?: number; max?: number },
  paramName?: string
): number {
  const raw = schema.step;
  const parsed =
    typeof raw === "number"
      ? raw
      : typeof raw === "string"
        ? Number(raw)
        : NaN;

  if (Number.isFinite(parsed) && parsed > 0) return parsed;

  if (paramName === "speed" && schema.min === -1 && schema.max === 1) return 0.1;
  return schema.type === "float" ? 0.1 : 1;
}

/** Snap a value onto the nearest step increment within [min, max]. */
export function snapToStep(
  value: number,
  step: number,
  min?: number,
  max?: number
): number {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) {
    return value;
  }

  const origin = min ?? 0;
  const snapped = Math.round((value - origin) / step) * step + origin;
  const decimals = countStepDecimals(step);
  const rounded =
    decimals === 0
      ? Math.round(snapped)
      : Number(snapped.toFixed(decimals));

  if (typeof min === "number" && rounded < min) return min;
  if (typeof max === "number" && rounded > max) return max;
  return rounded;
}

export function countStepDecimals(step: number): number {
  if (!Number.isFinite(step) || step >= 1) return 0;
  const asString = step.toString();
  if (asString.includes("e-")) {
    const exp = Number(asString.split("e-")[1]);
    return Number.isFinite(exp) ? exp : 2;
  }
  const parts = asString.split(".");
  return parts[1]?.length ?? 0;
}

export function formatStepValue(value: number, step: number): string {
  return value.toFixed(countStepDecimals(step));
}

